import React, { FC } from 'react'
import { GetServerSideProps } from 'next'
import chunk from 'lodash.chunk'
import useSWR from 'swr'

import { Layout } from '@components/layout'
import { HeadMetadata } from 'components/head-metadata'
import { HeaderSection } from '@components/header-section'
import { AuthorsList } from '@components/authors-list'

import { MenusApi, Menu } from '@services/api/menus'
import { AuthorIndexPagination, AuthorsApi } from '@services/api/authors'
import { Pagination } from '@components/shared/pagination'
import { convertObjectQueryParams } from '@utils/convert-object-query-params'
import { fetcherDataApi } from '@utils/fetcher-data-api'
import { AdsApi } from '@services/api/ads'
import { AdsIndexDto } from '@services/api/ads/types'
import { SponsorBanner } from '@components/sponsor-banner'

interface AuthorsPageProps {
  url: string
  data: AuthorIndexPagination
  main: Menu[]
  footer1: Menu[]
  footer2: Menu[]
  ads: AdsIndexDto[]
}

const AuthorsPage: FC<AuthorsPageProps> = ({
  url,
  data: initialData,
  main,
  footer1,
  footer2,
  ads
}) => {
  const { data } = useSWR(url, fetcherDataApi, {
    initialData
  })

  const { authors, total, page, pageSize } = data

  return (
    <Layout
      sponsorKeyword="home"
      mainMenus={main}
      footerMenus1={footer1}
      footerMenus2={footer2}
      logoUrl="/noticias"
      ad={ads.find(ad => ad.code === 'ad_sponsor_top')}
    >
      <HeadMetadata
        title="Portal Solar - Articulistas"
        metaTitle="Portal Solar - Articulistas"
        metaDescription="Acompanhe as novidades do setor de energia solar com a cobertura jornalística completa do nosso time de articulistas. Saiba tudo aqui no Portal Solar."
      />
      <HeaderSection currentDate={true} className="pt-4">
        <h1>Articulistas</h1>
      </HeaderSection>

      <div className="mt-4 mb-24">
        {chunk(authors, 6).map((chunk, index) => (
          <React.Fragment key={index}>
            <AuthorsList authors={chunk} className="mb-6 sm:mb-12" />
            {index === 0 && (
              <SponsorBanner
                config={ads.find(ad => ad.code === 'ad_sponsor_middle')}
                className="my-10"
              />
            )}
          </React.Fragment>
        ))}

        <SponsorBanner
          config={ads.find(ad => ad.code === 'ad_sponsor_bottom')}
          className="my-10"
        />
        <Pagination currentPage={page} pageSize={pageSize} total={total} />
      </div>
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  const page = Number(query.page || 1)
  const paramsPagination = {
    page,
    per: 15
  }

  const { data } = await AuthorsApi.index(paramsPagination)

  const url = convertObjectQueryParams({
    path: '/authors',
    object: paramsPagination
  })
  const menus = await MenusApi.all()

  const {
    data: { ads }
  } = await AdsApi.index({
    codes: ['ad_sponsor_top', 'ad_sponsor_middle', 'ad_sponsor_bottom']
  })

  return {
    props: {
      ...paramsPagination,
      data,
      url,
      ...menus,
      ads: ads || null
    }
  }
}

export default AuthorsPage
